import { useConfirmation } from '@/hooks/useConfirmation';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { createPortal } from 'react-dom';
/* global Element, BeforeUnloadEvent */
import { useCallback, useContext, useEffect, useRef, useState, type ReactNode, type ReactElement } from 'react';
import { Link, Outlet, useLocation, useNavigate, useBlocker, UNSAFE_DataRouterContext } from 'react-router-dom';
import { readSubmissionWorkspace, saveSubmissionWorkspace } from '@/lib/submissionWorkspaceStorage';
import { WorkspaceContext, type Entry, type Workspace } from '@/hooks/useSubmissionWorkspace';

function TabPlacement({ container, children }: { container?: HTMLElement | null; children: ReactNode }): ReactNode {
  return container ? createPortal(children, container) : children;
}

function NavigationGuard({ hasChanges, isBusy }: { hasChanges: () => boolean; isBusy: () => boolean }): ReactElement | null {
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    currentLocation.pathname + currentLocation.search !== nextLocation.pathname + nextLocation.search && hasChanges());
  const busy = isBusy();
  return blocker.state === 'blocked' ? createPortal(<ConfirmModal
    title={busy ? 'Save or upload in progress' : 'Unsaved changes'}
    message={busy ? 'Wait for the save or upload to finish before leaving this submission.' : 'You have unsaved changes. Leave this page and discard them?'}
    confirmLabel={busy ? 'Stay here' : 'Discard and leave'} variant={busy ? 'default' : 'destructive'}
    onConfirm={() => { if (busy || isBusy()) blocker.reset(); else blocker.proceed(); }} onCancel={() => blocker.reset()} />, document.body) : null;
}

export function OpenSubmissionTabs({ storageScope = 'local', headerContainer }: { storageScope?: string; headerContainer?: HTMLElement | null }): ReactElement {
  const { ask, dialog } = useConfirmation();
  const storageKey = `autoectd.open-submissions.v1:${storageScope}`;
  const [restored] = useState(() => readSubmissionWorkspace(storageKey));
  const location = useLocation();
  const dataRouter = useContext(UNSAFE_DataRouterContext);
  const navigate = useNavigate();
  const activeId = /^\/submissions\/([^/]+)/.exec(location.pathname)?.[1];
  const [entries, setEntries] = useState<Entry[]>(restored.entries);
  const dirty = useRef(false);
  const changedDialog = useRef<Element | null>(null);
  const views = useRef(restored.views);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const register = useCallback((entry: Entry) => {
    setEntries((old) => {
      const prior = old.find((item) => item.id === entry.id);
      if (prior && prior.name === entry.name && prior.sequence === entry.sequence && prior.url === entry.url) return old;
      return prior ? old.map((item) => item.id === entry.id ? entry : item) : [...old, entry];
    });
  }, []);
  const [workspace] = useState<Workspace>(() => ({ register, dirty, draftSources: new Set(), views: views.current, remove: (id) => {
    setEntries((old) => old.filter((entry) => entry.id !== id));
    for (const key of views.current.keys()) if (key.startsWith(`${id}:`)) views.current.delete(key);
  }, persist: () => saveSubmissionWorkspace(storageKey, entriesRef.current, views.current) }));
  useEffect(() => { workspace.persist(); }, [entries, workspace]);
  const hasChanges = () => dirty.current || Boolean(workspace.draftSources?.size) || Boolean(changedDialog.current?.isConnected);
  const isBusy = () => [...workspace.draftSources ?? []].some((source) => source.busy);
  const mayLeave = async () => {
    if (isBusy()) {
      await ask({ title: 'Save or upload in progress', message: 'Wait for the save or upload to finish before leaving this submission.', confirmLabel: 'Stay here' });
      return false;
    }
    return !hasChanges() || await ask({ title: 'Unsaved changes', message: 'You have unsaved changes. Close or leave this submission and discard them?', confirmLabel: 'Discard and leave', variant: 'destructive' });
  };
  useEffect(() => {
    const url = location.pathname + location.search + location.hash;
    setEntries((old) => old.map((item) => item.id === activeId && item.url !== url ? { ...item, url } : item));
    changedDialog.current = null;
  }, [activeId, location.pathname, location.search, location.hash]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty.current || workspace.draftSources?.size || changedDialog.current?.isConnected) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [workspace]);
  return <WorkspaceContext.Provider value={workspace}>
    {dialog}
    {dataRouter && <NavigationGuard hasChanges={hasChanges} isBusy={isBusy} />}
    <div onClickCapture={(event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest('a') : null;
      if (!dataRouter && link && link.getAttribute('href') !== location.pathname + location.search + location.hash && hasChanges()) {
        event.preventDefault();
        const href = link.getAttribute('href');
        void mayLeave().then((accepted) => { if (accepted && href) navigate(href); });
      }
    }} onChangeCapture={(event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const dialog = target.closest('[role="dialog"], dialog');
      // A dialog that MANAGES its own draft via useSubmissionDraft (marked
      // data-managed-draft) is tracked authoritatively by workspace.draftSources —
      // a change inside it must NOT also set this fallback flag, or the flag would
      // keep the unsaved-changes warning alive after that managed draft is
      // cancelled even though the (long-lived) dialog container is still open
      // (saved-target review item 4, proven in Chrome). The fallback remains for
      // ordinary unmanaged dialogs.
      if (dialog && dialog.closest('[data-managed-draft="true"]')) return;
      changedDialog.current = dialog ?? changedDialog.current;
    }}>
      <TabPlacement container={headerContainer}>
      {entries.length > 0 && <nav className="open-submissions" aria-label="Open submissions">
        {entries.map((entry) => <div key={entry.id} className={`open-submission${entry.id === activeId ? ' is-active' : ''}`}>
          <Link to={entry.url} aria-current={entry.id === activeId ? 'page' : undefined}
            onClick={(event) => {
              if (entry.id === activeId) event.preventDefault();
            }} title={`${entry.name} · Sequence ${entry.sequence ?? '—'}`}>
            <strong>{entry.name}</strong><span>Seq {entry.sequence ?? '—'}</span>
          </Link>
          <button type="button" aria-label={`Close ${entry.name} sequence ${entry.sequence ?? '—'}`}
            onClick={async () => {
              if (entry.id === activeId && hasChanges() && !(await mayLeave())) return;
              const remaining = entries.filter((item) => item.id !== entry.id);
              setEntries(remaining);
              for (const key of views.current.keys()) if (key.startsWith(`${entry.id}:`)) views.current.delete(key);
              if (entry.id === activeId) {
                dirty.current = false;
                workspace.draftSources?.clear();
                changedDialog.current = null;
                const index = entries.findIndex((item) => item.id === entry.id);
                navigate(remaining[Math.min(index, remaining.length - 1)]?.url ?? '/home');
              }
            }}>×</button>
        </div>)}
      </nav>}
      </TabPlacement>
      <Outlet key={activeId ?? 'other'} />
    </div>
  </WorkspaceContext.Provider>;
}
